import { useEffect, useState, type CSSProperties } from 'react';
import { useOrgSettings, useUpdateOrgSettings } from '../../api/org-settings.api';
import { useUpdateSmsAccount, type SmsProviderAccount } from '../../api/sms-accounts.api';

/**
 * Limits on campaign texts (JT1-1858). Texts outside these hours, or past a cap, wait rather than
 * being dropped, and the campaign page says why.
 */

const DAYS = [
  { code: 'MON', label: 'Monday' },
  { code: 'TUE', label: 'Tuesday' },
  { code: 'WED', label: 'Wednesday' },
  { code: 'THU', label: 'Thursday' },
  { code: 'FRI', label: 'Friday' },
  { code: 'SAT', label: 'Saturday' },
  { code: 'SUN', label: 'Sunday' },
];

/** When campaign texts may go out, in each recipient's time zone, and the per-recruiter daily cap. */
export function SmsWindowCard() {
  const settings = useOrgSettings().data?.data;
  const update = useUpdateOrgSettings();
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('18:00');
  const [days, setDays] = useState<string[]>(['MON', 'TUE', 'WED', 'THU', 'FRI']);
  const [cap, setCap] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setStart((settings.sms_window_start as string) || '09:00');
    setEnd((settings.sms_window_end as string) || '18:00');
    setDays(Array.isArray(settings.sms_send_days) ? (settings.sms_send_days as string[]) : ['MON', 'TUE', 'WED', 'THU', 'FRI']);
    setCap(settings.sms_daily_cap_per_recruiter != null ? String(settings.sms_daily_cap_per_recruiter) : '');
  }, [settings?.sms_window_start, settings?.sms_window_end, settings?.sms_send_days, settings?.sms_daily_cap_per_recruiter]);

  const toggle = (code: string) =>
    setDays((d) => (d.includes(code) ? d.filter((c) => c !== code) : DAYS.map((x) => x.code).filter((c) => c === code || d.includes(c))));

  const save = async () => {
    const perRecruiter = cap.trim() === '' ? null : Math.max(1, Math.floor(Number(cap)));
    await update.mutateAsync({ sms_window_start: start, sms_window_end: end, sms_send_days: days, sms_daily_cap_per_recruiter: perRecruiter });
    setSaved(true);
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <span style={headingStyle}>Text messages</span>
        <span style={mutedStyle}>
          Campaign texts only go out in these hours, by each person&rsquo;s own clock (worked out from their number).
          Texts due outside them wait until the next opening.
        </span>
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
        {DAYS.map((d) => {
          const on = days.includes(d.code);
          return (
            <button key={d.code} type="button" aria-pressed={on} aria-label={d.label} onClick={() => toggle(d.code)} style={dayStyle(on)}>
              {d.label.slice(0, 3)}
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Labelled label="Texts from">
          <input type="time" value={start} onChange={(e) => setStart(e.target.value)} style={inputStyle} />
        </Labelled>
        <Labelled label="Texts until">
          <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} style={inputStyle} />
        </Labelled>
        <Labelled label="Texts per recruiter per day">
          <input type="number" min={1} value={cap} placeholder="No limit" onChange={(e) => setCap(e.target.value)} style={{ ...inputStyle, width: '140px' }} />
        </Labelled>
        <button type="button" onClick={save} disabled={update.isPending || days.length === 0 || start >= end} style={buttonStyle}>
          Save text hours
        </button>
        {saved && <span style={mutedStyle}>Saved</span>}
      </div>
    </div>
  );
}

/** An SMS account's own caps: texts per day and a monthly spending limit (spend lags by a day). */
export function SmsAccountLimits({ account }: { account: SmsProviderAccount }) {
  const update = useUpdateSmsAccount(account.id);
  const [editing, setEditing] = useState(false);
  const [daily, setDaily] = useState(String(account.daily_message_cap));
  const [spend, setSpend] = useState(account.monthly_spend_cap_cents != null ? String(account.monthly_spend_cap_cents / 100) : '');

  const save = async () => {
    await update.mutateAsync({
      daily_message_cap: Math.max(1, Math.floor(Number(daily))),
      monthly_spend_cap_cents: spend.trim() === '' ? null : Math.round(Number(spend) * 100),
    });
    setEditing(false);
  };

  if (!editing) {
    return (
      <span style={mutedStyle}>
        Daily cap: {account.daily_message_cap.toLocaleString()} texts
        {account.monthly_spend_cap_cents != null && <> · Monthly limit: ${(account.monthly_spend_cap_cents / 100).toFixed(2)} (spend counted to yesterday)</>}
        {' '}
        <button type="button" onClick={() => setEditing(true)} style={linkStyle}>Edit limits</button>
      </span>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <Labelled label="Texts per day">
        <input type="number" min={1} value={daily} onChange={(e) => setDaily(e.target.value)} style={{ ...inputStyle, width: '110px' }} />
      </Labelled>
      <Labelled label="Monthly spending limit ($)">
        <input type="number" min={0} step="0.01" value={spend} placeholder="No limit" onChange={(e) => setSpend(e.target.value)} style={{ ...inputStyle, width: '140px' }} />
      </Labelled>
      <button type="button" onClick={save} disabled={update.isPending || !(Number(daily) >= 1)} style={buttonStyle}>Save limits</button>
      <button type="button" onClick={() => setEditing(false)} style={linkStyle}>Cancel</button>
    </div>
  );
}

function Labelled({ label, children }: { label: string; children: React.ReactElement }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
      {label}
      {children}
    </label>
  );
}

const cardStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', padding: 'var(--space-4)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-border-default)' };
const headingStyle: CSSProperties = { fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' };
const mutedStyle: CSSProperties = { fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' };
const inputStyle: CSSProperties = { height: 'var(--input-height)', padding: '0 var(--space-2)', border: '1px solid var(--input-border)', borderRadius: 'var(--input-radius)', fontFamily: 'var(--font-sans)', fontSize: 'var(--input-font-size)', backgroundColor: 'var(--input-bg)', color: 'var(--color-text-primary)' };
const buttonStyle: CSSProperties = { height: 'var(--input-height)', padding: '0 var(--space-4)', border: 'none', borderRadius: 'var(--btn-radius)', backgroundColor: 'var(--color-action-primary)', color: 'var(--color-action-primary-text)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer' };
const linkStyle: CSSProperties = { border: 'none', background: 'transparent', padding: 0, color: 'var(--color-text-link)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer' };
const dayStyle = (on: boolean): CSSProperties => ({
  minWidth: '44px', height: 'var(--btn-height)', borderRadius: 'var(--radius-input)',
  border: `1px solid ${on ? 'var(--color-action-primary)' : 'var(--color-border-default)'}`,
  backgroundColor: on ? 'var(--color-action-primary)' : 'var(--color-surface-white)',
  color: on ? 'var(--color-action-primary-text)' : 'var(--color-text-muted)',
  fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer',
});
